import * as React from 'react';

import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { AlertCircle, Loader2 } from 'lucide-react';

import { cn } from '../../utils/utils';
import { Button } from './button';

interface ConfirmActionDialogProps {
  warningTitle?: React.ReactNode;
  warningMessage?: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onConfirmAction: () => void;
  cancelActionButtonText?: string;
  confirmActionButtonText?: string;
  severity?: 'error' | 'warning' | 'info' | 'success';
  loading?: boolean;
  cancelActionButtonColor?: 'error' | 'secondary';
  // Test hooks, mirroring the uiComponents/ConfirmActionDialog variant so a
  // migrated dialog keeps its data-testid coverage (required for agent-browser).
  dialogTestId?: string;
  confirmButtonTestId?: string;
  cancelButtonTestId?: string;
  // Click-outside-to-cancel. Off by default so existing consumers (e.g.
  // Locations) keep Radix's interrupt behavior; opt in per dialog.
  dismissOnOverlayClick?: boolean;
  // Compact header (no icon background, description under the title). Off by
  // default so existing consumers keep the original look; opt in per dialog.
  compactHeader?: boolean;
}

const severityConfig = {
  error: {
    iconColor: 'text-red-500',
    iconBg: 'bg-red-50',
  },
  warning: {
    iconColor: 'text-yellow-500',
    iconBg: 'bg-yellow-50',
  },
  info: {
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-50',
  },
  success: {
    iconColor: 'text-green-500',
    iconBg: 'bg-green-50',
  },
};

export function ConfirmActionDialog({
  warningTitle,
  warningMessage,
  isOpen,
  onClose,
  onConfirmAction,
  cancelActionButtonText = 'Cancel',
  confirmActionButtonText = 'Confirm',
  severity = 'warning',
  loading = false,
  cancelActionButtonColor = 'secondary',
  dialogTestId,
  confirmButtonTestId,
  cancelButtonTestId,
  dismissOnOverlayClick = false,
  compactHeader = false,
}: ConfirmActionDialogProps) {
  const config = severityConfig[severity];

  return (
    <AlertDialog.Root
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
    >
      <AlertDialog.Portal container={document.body}>
        {/* Radix AlertDialog never dismisses on overlay click by default (it is
            meant to interrupt). Consumers can opt into backdrop-to-dismiss via
            dismissOnOverlayClick: a click outside cancels (same as Cancel,
            never confirms), so it is non-destructive. stopPropagation always
            keeps the click from reaching the page underneath. */}
        <AlertDialog.Overlay
          className='fixed inset-0 z-[9998] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          onClick={(e) => {
            e.stopPropagation();
            if (dismissOnOverlayClick) onClose();
          }}
        />
        <AlertDialog.Content
          className='fixed left-[50%] top-[50%] z-[9999] w-[473px] min-h-[190px] translate-x-[-50%] translate-y-[-50%] rounded-[20px] bg-white p-8 pb-6 shadow-[2px_4px_16px_rgba(15,59,96,0.18)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
          onClick={(e) => e.stopPropagation()}
          onEscapeKeyDown={onClose}
          data-testid={dialogTestId}
        >
          {compactHeader ? (
            // Compact: no icon background, description under the title (signature).
            <div className='flex items-start gap-[18px] pb-0'>
              <div className='flex h-8 w-8 items-center justify-center'>
                <AlertCircle className={cn('h-5 w-5', config.iconColor)} />
              </div>
              <div className='flex-1'>
                <AlertDialog.Title className='text-base font-medium leading-6 text-gray-900'>
                  {warningTitle}
                </AlertDialog.Title>
                <AlertDialog.Description className='mt-2 text-base leading-6 text-gray-600'>
                  {warningMessage}
                </AlertDialog.Description>
              </div>
            </div>
          ) : (
            // Default: icon in a colored circle, description below (Locations etc.).
            <>
              <div className='flex items-start gap-[18px] pb-0'>
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full',
                    config.iconBg,
                  )}
                >
                  <AlertCircle className={cn('h-5 w-5', config.iconColor)} />
                </div>
                <AlertDialog.Title className='text-base font-medium leading-6 text-gray-900'>
                  {warningTitle}
                </AlertDialog.Title>
              </div>
              <AlertDialog.Description className='ml-[58px] mt-2 text-base leading-6 text-gray-600'>
                {warningMessage}
              </AlertDialog.Description>
            </>
          )}

          {/* Actions */}
          <div className='ml-[58px] mt-10 flex justify-end gap-3'>
            <AlertDialog.Cancel asChild>
              <Button
                variant='outline'
                disabled={loading}
                data-testid={cancelButtonTestId}
                className={cn(
                  'px-4 py-2 h-10',
                  cancelActionButtonColor === 'error'
                    ? 'border-red-500 text-red-500 hover:bg-red-50 hover:border-red-600 focus:ring-red-500'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:ring-gray-500',
                )}
              >
                {cancelActionButtonText}
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <Button
                onClick={onConfirmAction}
                disabled={loading}
                data-testid={confirmButtonTestId}
                className={cn(
                  'px-4 py-2 h-10',
                  severity === 'error'
                    ? 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500'
                    : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
                )}
              >
                {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                {confirmActionButtonText}
              </Button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
